import PropTypes from "prop-types";
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { createPortal } from "react-dom";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  faDownload,
  faSync,
  faExpand,
  faCompress,
  faChartLine,
  faInfoCircle,
  faPlus,
  faTrash,
  faTimes,
  faCopy,
  faBolt,
  faEllipsisH,
  faList,
  faGrip,
  faFilter,
  faChevronDown,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MaterialsTable from "../MaterialsTable/MaterialsTable";
import ListingCardGrid from "./ListingCardGrid";
import ActionButton from "../ui/ActionButton";
import SearchBar from "../ui/SearchBar";
import AddListingPanel from "./AddListingPanel";
import QuickAddPanel from "./QuickAddPanel";
import GlobalViewPanel from "./GlobalViewPanel";
import DeleteConfirmModal from "../Modals/DeleteConfirmModal";
import {
  loadColumnVisibility,
  saveColumnVisibility,
} from "../../utils/columnVisibilityStorage";
import "../../style/MaterialsAccordion.css";

const MaterialsAccordion = forwardRef(
  (
    {
      materialsTableRef,
      listingsData,
      totalCount,
      setSelectedListing,
      darkMode,
      handleDataUpdate,
      handleRefresh,
      handleExport,
      onTableError,
      isRefreshing,
      onAddListing,
      onBulkAddListings,
      onDeleteListings,
      onToggleVisibility,
      onDetailPanelChange,
      onDisplayedRowChange,
      onWorkspaceChangesDetected,
      onOpenWorkspace,
      onRowSelected,
      searchTerm,
      onSearchChange,
      viewMode,
      onViewModeChange,
      statusFilter,
      onStatusFilterChange,
      materialFilter,
      onMaterialFilterChange,
      sortBy,
      onSortByChange,
      statusCounts,
      uniqueMaterials,
      onCardClick,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [tableHeight, setTableHeight] = useState(400);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [showDetailPanel, setShowDetailPanel] = useState(false); // kept for onDetailPanelChange compat
    const [displayedRow, setDisplayedRow] = useState(null);
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [isBulkAdding, setIsBulkAdding] = useState(false);
    const [duplicateData, setDuplicateData] = useState(null);
    const [quickAddInitial, setQuickAddInitial] = useState(null);
    const [showGlobalViewPanel, setShowGlobalViewPanel] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const containerRef = useRef(null);
    const addPanelRef = useRef(null);
    const quickAddPanelRef = useRef(null);
    const globalViewPanelRef = useRef(null);
    const overflowRef = useRef(null);
    const overflowBtnRef = useRef(null);
    const overflowDropdownRef = useRef(null);
    const [overflowPos, setOverflowPos] = useState({ top: 0, right: 0 });

    const [columnVisibility, setColumnVisibility] = useState(() => loadColumnVisibility());
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      openDetailPanel: (listingId) => {
        const listing = listingsData.find((l) => l.id === listingId);
        if (listing) {
          setSelectedRowIds([listingId]);
          setDisplayedRow(listing);
          setShowDetailPanel(true);
        }
      },
    }));

    // close overflow when clicking outside
    useEffect(() => {
      if (!overflowOpen) return;
      const onClickOutside = (e) => {
        const inBtn = overflowRef.current?.contains(e.target);
        const inDropdown = overflowDropdownRef.current?.contains(e.target);
        if (!inBtn && !inDropdown) {
          setOverflowOpen(false);
          setColumnMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }, [overflowOpen]);

    const handleDataUpdateWrapper = useCallback(
      (...args) => {
        const currentSelection = [...selectedRowIds];
        const currentDisplayedRow = displayedRow;
        const panelWasOpen = showDetailPanel;

        handleDataUpdate(...args);

        setTimeout(() => {
          if (currentSelection.length === 1 && panelWasOpen) {
            setSelectedRowIds(currentSelection);
            setDisplayedRow(currentDisplayedRow);
            setShowDetailPanel(true);
          }
        }, 0);
      },
      [selectedRowIds, displayedRow, showDetailPanel, handleDataUpdate]
    );

    useEffect(() => {
      if (selectedRowIds.length !== 1 && showDetailPanel) {
        setShowDetailPanel(false);
        setDisplayedRow(null);
      }
    }, [selectedRowIds, showDetailPanel]);

    // notify parent when a single row is selected (shows sidebar workspace tab)
    useEffect(() => {
      if (selectedRowIds.length === 1) {
        const row = listingsData.find((r) => r.id === selectedRowIds[0]);
        if (row && onRowSelected) onRowSelected(row);
      }
    }, [selectedRowIds, listingsData, onRowSelected]);

    useEffect(() => {
      if (onDetailPanelChange) onDetailPanelChange(showDetailPanel);
    }, [showDetailPanel, onDetailPanelChange]);

    useEffect(() => {
      if (onDisplayedRowChange && displayedRow) onDisplayedRowChange(displayedRow.id);
    }, [displayedRow, onDisplayedRowChange]);

    useEffect(() => {
      if (showAddPanel && addPanelRef.current) {
        setTimeout(() => addPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 450);
      }
    }, [showAddPanel]);

    useEffect(() => {
      if (showQuickAdd && quickAddPanelRef.current) {
        setTimeout(() => quickAddPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 450);
      }
    }, [showQuickAdd]);

    useEffect(() => {
      if (showGlobalViewPanel && globalViewPanelRef.current) {
        setTimeout(() => globalViewPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 450);
      }
    }, [showGlobalViewPanel]);

    useEffect(() => {
      if (showGlobalViewPanel && selectedRowIds.length < 2) setShowGlobalViewPanel(false);
    }, [selectedRowIds.length, showGlobalViewPanel]);

    useEffect(() => {
      saveColumnVisibility(columnVisibility);
    }, [columnVisibility]);

    const selectedRows = listingsData.filter((r) => selectedRowIds.includes(r.id));

    // visibility toggle eligibility
    // can't pause: expired (by status OR past deadline), completed, cancelled, or fully donated
    const TERMINAL_STATUSES = ["expired", "completed", "cancelled"];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const isPastDeadline = (r) => {
      if (!r.latest_pickup) return false;
      const d = new Date(r.latest_pickup);
      d.setHours(0, 0, 0, 0);
      return d < now;
    };
    const pausableRows = selectedRows.filter(
      (r) => !TERMINAL_STATUSES.includes(r.status?.toLowerCase())
        && !isPastDeadline(r)
        && (r.remaining_qty ?? 0) > 0
    );
    const allPaused = pausableRows.length > 0 && pausableRows.every((r) => r.status?.toLowerCase() === "paused");
    const allActive = pausableRows.length > 0 && pausableRows.every((r) => r.status?.toLowerCase() !== "paused");
    const canToggle = selectedRowIds.length > 0 && pausableRows.length === selectedRowIds.length && (allPaused || allActive);

    const ROW_HEIGHT = 51.4;
    const HEADER_HEIGHT = 56;
    const FOOTER_BUFFER = 18;
    const maxTableHeight = listingsData.length * ROW_HEIGHT + HEADER_HEIGHT + FOOTER_BUFFER;
    const isFullyExpanded = tableHeight >= maxTableHeight - 20;

    const handleMouseDown = useCallback(() => setIsDragging(true), []);
    const handleMouseUp = useCallback(() => setIsDragging(false), []);

    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setTableHeight(Math.max(200, Math.min(e.clientY - rect.top, maxTableHeight)));
      },
      [isDragging, maxTableHeight]
    );

    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "ns-resize";
        document.body.style.userSelect = "none";
      } else {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleOpenWorkspaceClick = (e) => {
      e?.stopPropagation();
      const row = selectedRowIds.length === 1
        ? listingsData.find((r) => r.id === selectedRowIds[0])
        : null;
      if (!row) return;
      if (onOpenWorkspace) onOpenWorkspace(row);
      setOverflowOpen(false);
    };

    const handleAddClick = (e) => {
      e?.stopPropagation();
      setOverflowOpen(false);
      setDuplicateData(null);
      setShowQuickAdd(false);
      if (showAddPanel) {
        addPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      setShowAddPanel(true);
    };

    const handleDuplicate = (e) => {
      e?.stopPropagation();
      setOverflowOpen(false);
      if (!selectedRowIds.length) return;

      if (selectedRowIds.length === 1) {
        const l = listingsData.find((r) => r.id === selectedRowIds[0]);
        if (!l) return;
        setDuplicateData({
          material: l.material || "", item_name: l.item_name || "",
          quantity: l.quantity ?? "", cost: l.cost ?? "",
          delivery_method: l.delivery_method || "pickup", transport_mode: l.transport_mode || "",
          delivery_instructions: l.delivery_instructions || "",
          contact_email: l.contact_email || "", contact_phone: l.contact_phone || "",
          address: l.address || "", city: l.city || "",
          share_contact: l.share_contact ?? true, use_default_address: l.use_default_address ?? true,
          pickup_time_start: (l.pickup_time_start || "09:00").slice(0, 5),
          pickup_time_end: (l.pickup_time_end || "17:00").slice(0, 5),
          available_days: l.available_days || { monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false },
          duplicated_from: l.id,
        });
        setShowQuickAdd(false);
        setShowAddPanel(true);
        setTimeout(() => addPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), showAddPanel ? 50 : 450);
        return;
      }

      const selected = selectedRowIds.map((id) => listingsData.find((r) => r.id === id)).filter(Boolean);
      const rows = selected.map((l) => ({
        id: crypto.randomUUID(), material: l.material || "", item_name: l.item_name || "",
        quantity: l.quantity ?? "", cost: l.cost ?? "", latest_pickup: "", duplicated_from: l.id,
      }));
      const first = selected[0];
      const allMatch = (fn) => selected.every((l) => JSON.stringify(fn(l)) === JSON.stringify(fn(first)));
      const batch = {
        pickup_time_start: allMatch((l) => (l.pickup_time_start || "09:00").slice(0, 5)) ? (first.pickup_time_start || "09:00").slice(0, 5) : "09:00",
        pickup_time_end: allMatch((l) => (l.pickup_time_end || "17:00").slice(0, 5)) ? (first.pickup_time_end || "17:00").slice(0, 5) : "17:00",
        available_days: allMatch((l) => l.available_days) ? (first.available_days || { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) : { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false },
        delivery_method: allMatch((l) => l.delivery_method) ? (first.delivery_method || "pickup") : "pickup",
        transport_mode: allMatch((l) => l.transport_mode) ? (first.transport_mode || "") : "",
        delivery_instructions: allMatch((l) => l.delivery_instructions) ? (first.delivery_instructions || "") : "",
        share_contact: allMatch((l) => l.share_contact) ? (first.share_contact ?? true) : true,
        use_default_address: allMatch((l) => l.use_default_address) ? (first.use_default_address ?? true) : true,
        address: first.address || "", city: first.city || "",
        contact_email: first.contact_email || "", contact_phone: first.contact_phone || "",
      };

      setQuickAddInitial({ rows, batch });
      setShowAddPanel(false);
      setDuplicateData(null);
      setShowQuickAdd(true);
      setTimeout(() => quickAddPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), showQuickAdd ? 50 : 450);
    };

    const handleQuickAddClick = (e) => {
      e?.stopPropagation();
      setOverflowOpen(false);
      if (showQuickAdd) {
        quickAddPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      setShowAddPanel(false);
      setDuplicateData(null);
      setQuickAddInitial(null);
      setShowQuickAdd(true);
    };

    const handleBulkSubmit = async (listings, batchSettings) => {
      setIsBulkAdding(true);
      try {
        const result = await onBulkAddListings(listings, batchSettings);
        if (result.success) {
          setShowQuickAdd(false);
          setSnackbar({ open: true, message: result.message, severity: "success" });
        } else {
          setSnackbar({ open: true, message: result.message, severity: "error" });
        }
      } catch (err) {
        console.error("bulk add error:", err);
        setSnackbar({ open: true, message: "An unexpected error occurred", severity: "error" });
      } finally {
        setIsBulkAdding(false);
      }
    };

    const handleAddSubmit = async (listingData) => {
      setIsAdding(true);
      try {
        const result = await onAddListing(listingData);
        if (result.success) {
          setShowAddPanel(false);
          setDuplicateData(null);
          setTimeout(() => {
            const el = document.documentElement.scrollTop > 0 ? document.documentElement : document.body;
            el.scrollTo?.({ top: 0, behavior: "smooth" });
          }, 100);
          setSnackbar({ open: true, message: result.message, severity: "success" });
        } else {
          setSnackbar({ open: true, message: result.message, severity: "error" });
        }
      } catch (err) {
        console.error("add listing error:", err);
        setSnackbar({ open: true, message: "An unexpected error occurred while adding the listing", severity: "error" });
      } finally {
        setIsAdding(false);
      }
    };

    const handleDeleteClick = (e) => {
      e?.stopPropagation();
      setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
      setIsDeleting(true);
      try {
        const result = await onDeleteListings(selectedRowIds);
        if (result.success) {
          setShowDeleteDialog(false);
          setSelectedRowIds([]);
          setSnackbar({ open: true, message: result.message, severity: "success" });
        } else {
          setSnackbar({ open: true, message: result.message, severity: "error" });
        }
      } catch (err) {
        console.error("delete error:", err);
        setSnackbar({ open: true, message: "An unexpected error occurred while deleting listings", severity: "error" });
      } finally {
        setIsDeleting(false);
      }
    };

    const handleToggleClick = async () => {
      if (!canToggle || isToggling) return;
      setIsToggling(true);
      try {
        const result = await onToggleVisibility(selectedRowIds, allActive);
        if (result.success) {
          setSelectedRowIds([]);
          setSnackbar({ open: true, message: result.message, severity: "success" });
        } else {
          setSnackbar({ open: true, message: result.message, severity: "error" });
        }
      } catch (err) {
        console.error("toggle visibility error:", err);
        setSnackbar({ open: true, message: "An unexpected error occurred", severity: "error" });
      } finally {
        setIsToggling(false);
      }
    };

    return (
      <div className="materials-accordion-container">
        <div className={`materials-accordion ${darkMode ? "dark" : "light"}`}>

          {/* unified header */}
          <div className="mat-header">
            <span className="mat-title">
              {t("List of Materials")}
              <span className="mat-count"> ({totalCount ?? listingsData.length})</span>
            </span>

            <div className="mat-spacer" />

            <SearchBar
              value={searchTerm}
              onChange={onSearchChange}
              placeholder={t("Search materials...")}
              darkMode={darkMode}
            />

            <div className="view-toggle-group">
              <button
                className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => onViewModeChange("table")}
                title={t("Table view")}
              >
                <FontAwesomeIcon icon={faList} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => onViewModeChange("cards")}
                title={t("Card view")}
              >
                <FontAwesomeIcon icon={faGrip} />
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* core actions: add, delete (conditional), refresh */}
            <ActionButton
              variant="secondary"
              icon={faPlus}
              iconOnly
              tooltip={t("Add Listing")}
              onClick={handleAddClick}
            />

            {selectedRowIds.length > 0 && (
              <ActionButton
                variant="danger"
                icon={faTrash}
                iconOnly
                tooltip={selectedRowIds.length === 1 ? t("Delete Listing") : t("Delete {{count}} Listings", { count: selectedRowIds.length })}
                onClick={handleDeleteClick}
              />
            )}

            {canToggle && (
              <ActionButton
                variant={allPaused ? "secondary" : "warning"}
                icon={allPaused ? faEye : faEyeSlash}
                iconOnly
                tooltip={
                  allPaused
                    ? selectedRowIds.length === 1 ? t("Show on Marketplace") : t("Show {{count}} on Marketplace", { count: selectedRowIds.length })
                    : selectedRowIds.length === 1 ? t("Hide from Marketplace") : t("Hide {{count}} from Marketplace", { count: selectedRowIds.length })
                }
                onClick={handleToggleClick}
                loading={isToggling}
              />
            )}

            <ActionButton
              variant="default"
              icon={faSync}
              iconOnly
              tooltip={t("Refresh")}
              onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              disabled={isRefreshing}
              spinning={isRefreshing}
            />

            {selectedRowIds.length > 0 && (
              <span className="selection-badge">
                {selectedRowIds.length} {t("selected")}
              </span>
            )}

            {/* overflow ••• */}
            <div className="overflow-menu-wrapper" ref={overflowRef}>
              <span ref={overflowBtnRef}>
                <ActionButton
                  variant={overflowOpen ? "success" : "default"}
                  icon={faEllipsisH}
                  iconOnly
                  tooltip={t("More actions")}
                  onClick={() => {
                    if (!overflowOpen && overflowBtnRef.current) {
                      const r = overflowBtnRef.current.getBoundingClientRect();
                      setOverflowPos({ top: r.bottom + window.scrollY + 6, right: window.innerWidth - r.right });
                    }
                    setOverflowOpen((o) => !o);
                  }}
                />
              </span>
              {overflowOpen && createPortal(
                <div
                  className={`overflow-dropdown ${darkMode ? "dark" : "light"}`}
                  ref={overflowDropdownRef}
                  style={{ position: "absolute", top: overflowPos.top, right: overflowPos.right, zIndex: 9999 }}
                >
                  <button className="overflow-item" onClick={handleQuickAddClick}>
                    <FontAwesomeIcon icon={faBolt} />
                    <span>{t("Quick Add")}</span>
                  </button>

                  {selectedRowIds.length > 0 && (
                    <button className="overflow-item" onClick={handleDuplicate}>
                      <FontAwesomeIcon icon={faCopy} />
                      <span>
                        {selectedRowIds.length === 1
                          ? t("Duplicate Listing")
                          : t("Duplicate {{count}} Listings", { count: selectedRowIds.length })}
                      </span>
                    </button>
                  )}

                  <div className="overflow-divider" />

                  <button className="overflow-item" onClick={() => { handleExport(columnVisibility); setOverflowOpen(false); }}>
                    <FontAwesomeIcon icon={faDownload} />
                    <span>{t("Export CSV")}</span>
                  </button>

                  <button className="overflow-item" onClick={() => setColumnMenuOpen(o => !o)}>
                    <FontAwesomeIcon icon={faFilter} />
                    <span>{t("Columns")}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`overflow-chevron ${columnMenuOpen ? "open" : ""}`} />
                  </button>

                  {columnMenuOpen && (
                    <div className="overflow-col-inline">
                      {[
                        { field: "material", label: t("Material"), mandatory: true },
                        { field: "quantity", label: t("Quantity Total") },
                        { field: "remaining_qty", label: t("Quantity Left") },
                        { field: "cost", label: t("Cost") },
                        { field: "status", label: t("Status") },
                        { field: "latest_pickup", label: t("Pickup Deadline") },
                        { field: "delivery_instructions", label: t("Delivery Instructions") },
                        { field: "date_posted", label: t("Date Posted") },
                      ].map(col => (
                        <label key={col.field} className={`overflow-col-item ${col.mandatory ? "mandatory" : ""}`}>
                          <input type="checkbox" checked={col.mandatory || columnVisibility[col.field] !== false} disabled={col.mandatory}
                            onChange={() => {
                              if (col.mandatory) return;
                              setColumnVisibility(prev => ({ ...prev, [col.field]: prev[col.field] === false ? true : false }));
                            }}
                          />
                          <span>{col.label}</span>
                          {col.mandatory && <span className="overflow-col-required">{t("Required")}</span>}
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="overflow-divider" />

                  {viewMode === "table" && (
                    <button className="overflow-item" onClick={() => { setTableHeight(isFullyExpanded ? 400 : maxTableHeight); setOverflowOpen(false); }}>
                      <FontAwesomeIcon icon={isFullyExpanded ? faCompress : faExpand} />
                      <span>{isFullyExpanded ? t("Collapse Table") : t("Expand Table")}</span>
                    </button>
                  )}

                  {selectedRowIds.length === 1 && (
                    <>
                      <div className="overflow-divider" />
                      <button
                        className="overflow-item"
                        onClick={handleOpenWorkspaceClick}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>{t("Open Workspace")}</span>
                      </button>
                    </>
                  )}

                  {selectedRows.length > 1 && (
                    <>
                      <div className="overflow-divider" />
                      <button
                        className="overflow-item"
                        onClick={() => {
                          showGlobalViewPanel ? setShowGlobalViewPanel(false) && setSelectedRowIds([]) : setShowGlobalViewPanel(true);
                          setOverflowOpen(false);
                        }}
                      >
                        <FontAwesomeIcon icon={showGlobalViewPanel ? faTimes : faChartLine} />
                        <span>{showGlobalViewPanel ? t("Exit Global View") : t("Global View")}</span>
                      </button>
                    </>
                  )}
                </div>,
                document.body
              )}
            </div>
          </div>

          {/* card view: filter bar + grid */}
          {viewMode === "cards" && (
            <>
              <div className={`donate-filter-bar ${darkMode ? "dark" : "light"}`}>
                <div className={`status-filter-tabs ${darkMode ? "dark" : "light"}`}>
                  {[
                    { key: "all", label: t("All") },
                    { key: "new", label: t("New") },
                    { key: "available", label: t("Available") },
                    { key: "low", label: t("Low") },
                    { key: "almost_out", label: t("Almost Out") },
                    { key: "donated", label: t("Donated") },
                    { key: "paused", label: t("Paused") },
                  ]
                    .filter((tab) => tab.key === "all" || statusCounts[tab.key] > 0)
                    .map((tab) => {
                      const isAll = tab.key === "all";
                      const isActive = isAll ? statusFilter.size === 0 : statusFilter.has(tab.key);
                      return (
                        <button
                          key={tab.key}
                          className={`status-filter-tab ${isActive ? "active" : ""}`}
                          onClick={() => {
                            if (isAll) {
                              onStatusFilterChange(new Set());
                            } else {
                              onStatusFilterChange((prev) => {
                                const next = new Set(prev);
                                next.has(tab.key) ? next.delete(tab.key) : next.add(tab.key);
                                return next;
                              });
                            }
                          }}
                        >
                          {tab.label}
                          <span className="status-filter-count">{statusCounts[tab.key] || 0}</span>
                        </button>
                      );
                    })}
                </div>

                <div className="filter-dropdowns-row">
                  <div className="filter-dropdown-group">
                    <label className="filter-dropdown-label">{t("Material")}</label>
                    <select
                      className={`filter-dropdown ${darkMode ? "dark" : "light"}`}
                      value={materialFilter}
                      onChange={(e) => onMaterialFilterChange(e.target.value)}
                    >
                      <option value="all">{t("All Materials")}</option>
                      {uniqueMaterials.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>

                  <div className="filter-dropdown-group">
                    <label className="filter-dropdown-label">{t("Sort By")}</label>
                    <select
                      className={`filter-dropdown ${darkMode ? "dark" : "light"}`}
                      value={sortBy}
                      onChange={(e) => onSortByChange(e.target.value)}
                    >
                      <option value="newest">{t("Newest First")}</option>
                      <option value="oldest">{t("Oldest First")}</option>
                      <option value="pickup_asc">{t("Pickup ↑ Soonest")}</option>
                      <option value="pickup_desc">{t("Pickup ↓ Latest")}</option>
                      <option value="qty_desc">{t("Quantity ↓ High")}</option>
                      <option value="qty_asc">{t("Quantity ↑ Low")}</option>
                      <option value="value_desc">{t("Value ↓ High")}</option>
                      <option value="value_asc">{t("Value ↑ Low")}</option>
                    </select>
                  </div>

                  {(statusFilter.size > 0 || materialFilter !== "all" || sortBy !== "newest") && (
                    <button
                      className={`filter-clear-btn ${darkMode ? "dark" : "light"}`}
                      onClick={() => {
                        onStatusFilterChange(new Set());
                        onMaterialFilterChange("all");
                        onSortByChange("newest");
                      }}
                    >
                      {t("Clear Filters")}
                    </button>
                  )}
                </div>
              </div>

              <div className="mat-card-body">
                {listingsData.length === 0 ? (
                  <div className={`listing-grid__empty ${darkMode ? "dark" : "light"}`}>
                    <p>
                      {searchTerm || statusFilter.size > 0 || materialFilter !== "all"
                        ? t("No listings match your filters")
                        : t("No listings found")}
                    </p>
                  </div>
                ) : (
                  <ListingCardGrid listings={listingsData} darkMode={darkMode} onCardClick={onCardClick} />
                )}
              </div>
            </>
          )}

          {/* table view */}
          {viewMode === "table" && (
            <div className="table-section">
              <div ref={containerRef} className="table-container-wrapper">
                <div
                  className={`table-container ${isRefreshing ? "refreshing" : ""}`}
                  style={{
                    height: `${tableHeight}px`,
                    maxHeight: `${tableHeight}px`,
                    overflow: isFullyExpanded ? "hidden" : "auto",
                  }}
                >
                  <MaterialsTable
                    ref={materialsTableRef}
                    data={listingsData}
                    onRowSelect={setSelectedListing}
                    darkMode={darkMode}
                    onDataUpdate={handleDataUpdateWrapper}
                    onError={onTableError}
                    onSelectionChange={setSelectedRowIds}
                    externalSelection={selectedRowIds}
                    columnVisibility={columnVisibility}
                    height="100%"
                  />
                </div>

                <div className={`resize-handle ${isDragging ? "dragging" : ""}`} onMouseDown={handleMouseDown}>
                  <div className="resize-handle-bar" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* workspace panel now lives at /donor/workspace */}

        {showAddPanel && (
          <div ref={addPanelRef} className="panel-slide-in">
            <AddListingPanel
              key={duplicateData ? `dup-${selectedRowIds[0]}` : "new"}
              darkMode={darkMode}
              onClose={() => { setShowAddPanel(false); setDuplicateData(null); }}
              onSubmit={handleAddSubmit}
              isSubmitting={isAdding}
              listingsData={listingsData}
              initialData={duplicateData}
            />
          </div>
        )}

        {showQuickAdd && (
          <div ref={quickAddPanelRef} className="panel-slide-in">
            <QuickAddPanel
              darkMode={darkMode}
              onClose={() => { setShowQuickAdd(false); setQuickAddInitial(null); }}
              onSubmit={handleBulkSubmit}
              isSubmitting={isBulkAdding}
              initialRows={quickAddInitial?.rows}
              initialBatch={quickAddInitial?.batch}
            />
          </div>
        )}

        {showGlobalViewPanel && (
          <div ref={globalViewPanelRef} className="panel-slide-in">
            <GlobalViewPanel
              selectedListings={selectedRows}
              darkMode={darkMode}
              onClose={() => setShowGlobalViewPanel(false)}
            />
          </div>
        )}

        <DeleteConfirmModal
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          count={selectedRowIds.length}
          darkMode={darkMode}
          isDeleting={isDeleting}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MuiAlert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {t(snackbar.message)}
          </MuiAlert>
        </Snackbar>
      </div>
    );
  }
);

MaterialsAccordion.displayName = "MaterialsAccordion";

MaterialsAccordion.propTypes = {
  materialsTableRef: PropTypes.object.isRequired,
  listingsData: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  setSelectedListing: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
  handleDataUpdate: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  handleExport: PropTypes.func.isRequired,
  onTableError: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool,
  onAddListing: PropTypes.func.isRequired,
  onBulkAddListings: PropTypes.func.isRequired,
  onDeleteListings: PropTypes.func.isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
  onDetailPanelChange: PropTypes.func,
  onDisplayedRowChange: PropTypes.func,
  onWorkspaceChangesDetected: PropTypes.func,
  onOpenWorkspace: PropTypes.func,
  onRowSelected: PropTypes.func,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  statusFilter: PropTypes.instanceOf(Set),
  onStatusFilterChange: PropTypes.func,
  materialFilter: PropTypes.string,
  onMaterialFilterChange: PropTypes.func,
  sortBy: PropTypes.string,
  onSortByChange: PropTypes.func,
  statusCounts: PropTypes.object,
  uniqueMaterials: PropTypes.array,
  onCardClick: PropTypes.func,
};

export default MaterialsAccordion;
